import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const eur = (v) => Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export const canSeePrices = (user) => user?.role === "agent" || user?.role === "admin";

function PriceViewModal({ title, params, onClose }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/prices", { params })
      .then((r) => setEntries(r.data))
      .catch(() => setError(true));
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onEsc, true);
    return () => window.removeEventListener("keydown", onEsc, true);
  }, [params, onClose]);

  return createPortal(
    <div className="tm-modal fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="agent-price-modal">
      <div className="tm-card p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="tm-tag inline-block mb-3">Listino prezzi · riservato</div>
            <h3 className="font-heading text-2xl text-white tracking-tighter">{title}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-2xl w-11 h-11 shrink-0 flex items-center justify-center" aria-label="Chiudi" data-testid="agent-price-close">✕</button>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_6rem_9rem] gap-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 pb-2 border-b border-slate-700">
          <span>Descrizione completa</span><span>UM</span><span className="text-right">Prezzo unit. netto</span>
        </div>
        <div className="divide-y divide-slate-800" data-testid="agent-price-rows">
          {error && <div className="py-4 text-red-400 text-sm">Impossibile caricare il listino.</div>}
          {!error && entries === null && <div className="py-4 text-slate-500 text-sm">Caricamento…</div>}
          {entries?.length === 0 && <div className="py-4 text-slate-500 text-sm" data-testid="agent-price-empty">Nessun listino disponibile.</div>}
          {entries?.map((e) => (
            <div key={e.id} className="py-3 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_6rem_9rem] gap-x-3 gap-y-1 items-center" data-testid={`agent-price-row-${e.id}`}>
              <div className="text-slate-200 text-sm whitespace-pre-line col-span-2 sm:col-span-1">{e.description}</div>
              <div className="font-mono text-xs text-slate-400 uppercase">{e.unit}</div>
              <div className="text-white font-medium text-right">{eur(e.unit_price)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PriceButton({ productId, productName, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [params] = useState(() => ({ product_id: productId }));
  if (!canSeePrices(user)) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`tm-btn-outline text-sm !border-orange-700 !text-orange-300 ${className}`}
        data-testid={`agent-price-btn-${productId}`}
      >
        € Vedi listino
      </button>
      {open && <PriceViewModal title={productName} params={params} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ExtraPricesButton({ className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [params] = useState({ standalone: true });
  if (!canSeePrices(user)) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`tm-btn-outline ${className}`} data-testid="agent-extra-prices-btn">
        € Listini extra
      </button>
      {open && <PriceViewModal title="Articoli fuori catalogo" params={params} onClose={() => setOpen(false)} />}
    </>
  );
}
